/**
 * cache/sites.ts — THE cache site catalog (Phase 6a).
 *
 * A **site** is a place on a planet's surface where a cache aperture stands. Sites are
 * pure `f(planet seed, site index)` — never persisted, never transmitted — exactly like
 * `planetNodes()` derives resource deposits in `../resources.ts`. That file is the model
 * this one is built on, down to the rejection idiom:
 *
 *   > "Draws above stay fixed-order, so skipping keeps the rng stream (and ids) aligned."
 *
 * We take that discipline one step further. `planetNodes` runs ONE stream for the whole
 * planet, so its alignment property is a convention a future edit can silently break (add
 * a draw inside the loop after a `continue` and every later node moves). Here each site
 * draws from its OWN sub-stream, `rngFromString(`${def.seed}:cache-site:${i}`)` — so a
 * site's direction, tier and theme are a function of `i` ALONE. Rejection rules can then
 * be added, removed or reordered and the surviving sites keep their exact ids, positions
 * and rolls. That matters because a site id is a durable player-facing handle: it is
 * embedded in the instance id (`cache:<site>:<key>:<epoch>`), so shifting ids would
 * re-point completion records and cooldowns at the wrong holes in the ground.
 *
 * ── SITE ID FORMAT ────────────────────────────────────────────────────────────────────
 *
 * The Phase 3 grammar (`universe.ts`) allows `[A-Za-z0-9_.-]{1,64}` per token, with ':'
 * reserved as the id separator — so a site token spells the planet with dots. A naive
 * `planetId.replace(/:/g, ".")` is reversible but too LONG: a sealed-frontier moon's id
 * (`hyperlight:sealed:v1:<43-char seed>:planet:N:moon:K`) is 81 characters and would be
 * unrepresentable, i.e. the entire sealed frontier could host no caches. So the fixed
 * literal prefixes are coded to one character each:
 *
 *     token := "p" <tag> <systemPart-with-':'→'.'> ".p" <planetIdx> [ ".m" <moonIdx> ] "." <siteIdx>
 *     tag   := "s" (hyperlight:sys:)  "h" (hyperlight:home:)  "x" (hyperlight:sealed:v1:)  "u" (raw)
 *
 *     hyperlight:sys:0:0:0:planet:2            site 5  →  ps0.0.0.p2.5
 *     hyperlight:sys:-4:1:-2:planet:3:moon:1   site 11 →  ps-4.1.-2.p3.m1.11
 *     hyperlight:home:<uid>:planet:0           site 2  →  ph<uid>.p0.2
 *     hyperlight:home:<uid>.<cid>:planet:0     site 2  →  ph<uid>.<cid>.p0.2
 *
 * **Reversibility proof** (`parseSiteId` is the inverse of {@link cacheSiteId}):
 *   1. Outside the home namespace no generated planet id contains '.', so ':'↔'.' is a
 *      bijection on the system part (encode throws if a caller ever violates this — loud,
 *      never a silent collision). HOME system parts contain no ':' and may contain the
 *      one `<uid>.<cid>` dot, so they ride the token verbatim in both directions.
 *   2. The tag is exactly one character and every code is distinct, so the prefix is
 *      recovered unambiguously; unrecognised ids take the `u` (verbatim) branch, and no
 *      `u`-token can collide with an `s`/`h`/`x` token because the tag differs.
 *   3. The structural tail `.p<d>[.m<d>].<d>` is ANCHORED at the end of the token and the
 *      system part is matched greedily, so the parser always splits at the LAST `.p<d>` —
 *      which is precisely where the encoder put it, whatever the system part contains.
 *   4. Every field is non-empty and drawn from a disjoint alphabet at its position, so
 *      distinct (planetId, index) pairs give distinct tokens.
 * `cache-sites.test.mts` pins all four legs against REAL `PlanetDef`s from
 * `generateSystem` / `generateHomeSystem` / `generateSealedSystem`, and round-trips a
 * token through the full Phase 3 id grammar (`cacheSystemId` → `parseSystemRef`).
 *
 * ── WHAT THIS FILE DELIBERATELY DOES NOT DO ───────────────────────────────────────────
 * No homestead-claim avoidance: homestead plots are AUTHORED world data, and consulting
 * them would make the catalog impure (and unavailable to a client that has not synced the
 * registry). Aperture-vs-plot arbitration belongs to the runtime presence layer (P6b),
 * not to the seed function.
 */
import { rngFromString, range, type Rng } from "../rng.js";
import * as pmath from "../pmath.js";
import { PlanetSampler, type PlanetDef, type TerrainSample } from "../universe.js";
import { settlementInfluenceAt } from "../worldgen/settlements.js";
import type { CacheTheme } from "./types.js";

/** One aperture's worth of world presence. Pure `f(def.seed, i)`. */
export interface CacheSite {
  /** Colon-free token: the `<site>` field of `cache:<site>:<key>:<epoch>`. */
  id: string;
  /** Index into `SystemDef.planets` (moons included — they are appended there). */
  planet: number;
  /** Unit direction from the planet centre; drape with the sampler for a position. */
  dir: [number, number, number];
  /** Run length: 0 = short (3–5 min), 1 = long (10–15 min). */
  tier: number;
  theme: CacheTheme;
}

// ── Density (exported so Phase 8 can tune against the ERA harness, not by eye) ───────
//
// The plan's target is "fairly common — roughly one site per few minutes of surface
// travel", calibrated as ~2.5× the deposit count `planetNodes()` draws for a body of the
// same size (12–20 planet / 6–10 moon). These are CANDIDATE counts: the rejection rules
// below take a real bite out of them (a wet terran world loses most of its draws to the
// sea test), which is exactly how `planetNodes` behaves too.
//
// Unlike `planetNodes`, the count scales with the body: WORLD_SCALE'd planets span
// 8–21 km of radius, and a fixed count would make the big worlds feel empty. Scaling is
// LINEAR in radius rather than in area (area would swing the count ~7× across the roster
// and bury the small worlds).

/** Candidate draws for a full planet at {@link SITE_RADIUS_REF_PLANET_M}. */
export const SITE_COUNT_PLANET: readonly [number, number] = [30, 50];
/** Candidate draws for a moon at {@link SITE_RADIUS_REF_MOON_M}. */
export const SITE_COUNT_MOON: readonly [number, number] = [15, 25];
/** Mid-roster planet radius (metres) — the radius at which the bands above apply. */
export const SITE_RADIUS_REF_PLANET_M = 14_500;
/** Mid-roster moon radius (metres). */
export const SITE_RADIUS_REF_MOON_M = 2_750;
/** No body, however tiny, draws fewer candidates than this. */
export const SITE_COUNT_FLOOR = 6;

// ── Rejection thresholds (each check is a `continue`, never a re-draw) ───────────────

/**
 * A site must stand at least this far ABOVE sea level, in metres of radius. 40 m clears
 * the shoreline band (surf, wading depth and the beach the water shader fades across),
 * so an aperture never opens into the tide.
 */
export const SITE_SEA_MARGIN_M = 40;
/**
 * Minimum surface-normal · radial dot. 0.94 ≈ 20° of slope — an aperture needs a pad the
 * player can stand and fight on, and 20° is the shallow end of the "steep site" gate the
 * settlement grower uses for the same reason. Measured over
 * {@link SITE_SLOPE_EPS_M}, not sub-metre, so a single boulder-sized wrinkle does not
 * veto an otherwise flat shelf.
 */
export const SITE_SLOPE_MIN_DOT = 0.94;
/** Arc (metres) the slope probe differences over — roughly an aperture pad's footprint. */
export const SITE_SLOPE_EPS_M = 4;
/** Settlement-influence slack, metres. Matches `planetNodes()` exactly. */
export const SITE_SETTLEMENT_SLACK_M = 10;
/**
 * Minimum great-circle chord between two ACCEPTED sites, metres. A tier-1 layout is
 * ~190 m across and apertures should read as separate destinations, not a cluster;
 * 1.2 km is ~6 aperture-fields apart and still far below the ~7 km mean spacing the
 * density above produces, so it trims clusters without thinning the catalog.
 *
 * NOTE the alignment property: this is the one rule that depends on other sites, and it
 * depends only on ones already ACCEPTED — never on rejected candidates, and never on the
 * rng — so it cannot perturb any site's own draws.
 */
export const SITE_MIN_SEPARATION_M = 1_200;

// ── Tier ─────────────────────────────────────────────────────────────────────────────

/** P(tier 1). Tier 0 is the common short run; tier 1 is the 10–15 minute expedition. */
export const SITE_TIER1_CHANCE = 0.2;

/** Tier from a site's tier roll ∈ [0,1). */
export function siteTierFor(roll: number): number {
  return roll < SITE_TIER1_CHANCE ? 1 : 0;
}

// ── Theme ────────────────────────────────────────────────────────────────────────────

/** Weight floor every theme keeps, so all three stay possible everywhere. */
export const SITE_THEME_BASE_W = 1;
/** How hard the biome may lean the roll on top of the floor. */
export const SITE_THEME_BIAS_W = 2.5;

/** Module-scope scratch — single-threaded, matching PlanetSampler's own pattern. */
const SCRATCH: TerrainSample = {
  h: 0, temp: 0, moist: 0, river: 0, mountain: 0,
  flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8,
};
const NORMAL: number[] = [0, 0, 0];

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Biome-biased theme roll — exported because the server wants exactly this function
 * (`server/src/cache.ts` carries a `TODO(P6)` FNV placeholder over `DRESSED_THEMES`).
 * All three themes are always reachable: the worst-case share is
 * `BASE / (3·BASE + BIAS)` ≈ 12%, so no biome can starve a theme out.
 *
 *  - hot + dry  → `ruin`   (desert and rock: broken surface architecture)
 *  - mountains  → `hollow` (carved into the massif)
 *  - temperate  → `vault`  (the sealed, built thing in liveable country)
 */
export function siteThemeFor(
  sampler: PlanetSampler, dx: number, dy: number, dz: number, roll: number,
): CacheTheme {
  const s = sampler.sample(dx, dy, dz, SCRATCH, true);
  const warm = clamp01((s.temp + 0.1) / 0.9);        // 0 below −0.1, 1 at +0.8
  const dry = clamp01((0.1 - s.moist) / 0.8);        // 1 when parched
  const mtn = clamp01(s.mountain);
  // Temperate = mild AND not parched AND not a mountainside.
  const mild = clamp01(1 - Math.abs(s.temp) / 0.6);
  const wet = clamp01((s.moist + 0.4) / 0.8);
  const temperate = mild * wet * (1 - mtn);

  const B = SITE_THEME_BASE_W, K = SITE_THEME_BIAS_W;
  const wRuin = B + K * clamp01(0.6 * dry + 0.4 * warm);
  const wVault = B + K * clamp01(temperate);
  const wHollow = B + K * mtn;
  const total = wRuin + wVault + wHollow;
  let r = roll * total;
  if ((r -= wRuin) < 0) return "ruin";
  if ((r -= wVault) < 0) return "vault";
  return "hollow";
}

// ── Site ids ─────────────────────────────────────────────────────────────────────────

/** `PlanetDef.id` is its seed: `<systemSeed>:planet:<i>` (+ `:moon:<k>` for a moon). */
const PLANET_ID_RE = /^(.*):planet:(\d{1,3})(?::moon:(\d{1,3}))?$/;
/** Site token: see the header for the grammar and the reversibility proof. */
const SITE_ID_RE = /^p([shxu])(.*)\.p(\d{1,3})(?:\.m(\d{1,3}))?\.(\d{1,4})$/;
/** Longest-first: `sealed:v1:` must be tried before any shorter shared prefix. */
const PREFIX_CODES: readonly (readonly [string, string])[] = [
  ["hyperlight:sealed:v1:", "x"],
  ["hyperlight:sys:", "s"],
  ["hyperlight:home:", "h"],
];
/** Verbatim fallback for a system seed outside the known namespaces. */
const RAW_CODE = "u";
/** Phase 3's token charset (universe.ts `CACHE_TOKEN_RE`), restated as a guard. */
const TOKEN_RE = /^[A-Za-z0-9_.-]{1,64}$/;
/** Ceiling on the per-planet site index (keeps the token bounded and `\d{1,4}` exact). */
export const SITE_INDEX_MAX = 9999;

/**
 * Build the site token for `(planetId, index)`. **Throws** rather than minting a token
 * that cannot round-trip: a site whose id does not parse is a cache nobody can enter, and
 * the Phase 3 lesson (`cacheSystemId`) is that those failures must be loud.
 */
export function cacheSiteId(planetId: string, index: number): string {
  const m = PLANET_ID_RE.exec(planetId);
  if (!m) throw new Error(`cacheSiteId: unrecognised planet id "${planetId}"`);
  if (!Number.isInteger(index) || index < 0 || index > SITE_INDEX_MAX) {
    throw new Error(`cacheSiteId: invalid site index ${index}`);
  }
  const sys = m[1];
  let tag = RAW_CODE, rest = sys;
  for (const [prefix, code] of PREFIX_CODES) {
    if (sys.startsWith(prefix)) { tag = code; rest = sys.slice(prefix.length); break; }
  }
  // Reversibility precondition (1): the '.'↔':' mapping must be a bijection.
  // The HOME namespace is the one place a legitimate '.' occurs — the
  // `<uid>.<cid>` character separator (multi-character accounts). Its system
  // part never contains ':', so it rides the token VERBATIM instead of via the
  // mapping (parseSiteId mirrors this), and the '.' ban applies to the rest.
  if (tag === "h") {
    if (!/^[A-Za-z0-9_-]{1,64}(?:\.c\d{1,3})?$/.test(rest)) {
      throw new Error(`cacheSiteId: malformed home planet id "${planetId}"`);
    }
  } else if (sys.includes(".")) {
    throw new Error(`cacheSiteId: planet id contains '.': "${planetId}"`);
  }
  const moon = m[3] === undefined ? "" : `.m${m[3]}`;
  const token = `p${tag}${rest.replace(/:/g, ".")}.p${m[2]}${moon}.${index}`;
  if (!TOKEN_RE.test(token)) {
    throw new Error(`cacheSiteId: "${planetId}"#${index} does not fit a cache token (${token.length} chars)`);
  }
  return token;
}

/** The inverse of {@link cacheSiteId}. Null for anything that is not a site token. */
export function parseSiteId(token: unknown): { planetId: string; index: number } | null {
  if (typeof token !== "string" || !TOKEN_RE.test(token)) return null;
  const m = SITE_ID_RE.exec(token);
  if (!m) return null;
  const tag = m[1];
  let prefix = "";
  if (tag !== RAW_CODE) {
    const hit = PREFIX_CODES.find(([, code]) => code === tag);
    if (!hit) return null;
    prefix = hit[0];
  }
  // Home system parts never contain ':' and may legitimately contain the
  // `<uid>.<cid>` dot — carried verbatim (the encoder's mirror rule).
  const sys = tag === "h" ? m[2] : m[2].replace(/\./g, ":");
  const moon = m[4] === undefined ? "" : `:moon:${m[4]}`;
  return { planetId: `${prefix}${sys}:planet:${m[3]}${moon}`, index: Number(m[5]) };
}

/** The per-site sub-stream seed. Exported so a consumer (or a test) can re-derive a
 *  site's draws from `(def, i)` alone — that IS the alignment guarantee. */
export function siteSeed(def: PlanetDef, i: number): string {
  return `${def.seed}:cache-site:${i}`;
}

/** Candidate draws for a body — exported for the P8 density pass. */
export function siteCandidateCount(def: PlanetDef): number {
  const moon = !!def.parentId;
  const rng: Rng = rngFromString(`${def.seed}:cache-sites`);
  const band = moon ? SITE_COUNT_MOON : SITE_COUNT_PLANET;
  const ref = moon ? SITE_RADIUS_REF_MOON_M : SITE_RADIUS_REF_PLANET_M;
  const base = range(rng, band[0], band[1]);
  return Math.max(SITE_COUNT_FLOOR, Math.round((base * def.radius) / ref));
}

/**
 * The cache sites on one planet — pure function of its seed, in the shape of
 * `planetNodes()`. Moons run about half a planet's count (the same precedent).
 *
 * Every candidate `i` draws from its own sub-stream FIRST and is tested SECOND, so a
 * rejected candidate is simply absent: no surviving site's id, direction, tier or theme
 * depends on any other candidate's fate.
 */
export function cacheSites(def: PlanetDef, planetIndex: number): CacheSite[] {
  const count = siteCandidateCount(def);
  const sampler = new PlanetSampler(def);
  const R = def.radius;
  const sites: CacheSite[] = [];
  for (let i = 0; i < count; i++) {
    // --- every draw for this site, and nothing else, from this site's sub-stream ---
    const r = rngFromString(siteSeed(def, i));
    let x = r() * 2 - 1;
    let y = r() * 2 - 1;
    let z = r() * 2 - 1;
    const tierRoll = r();
    const themeRoll = r();
    const l = pmath.hypot(x, y, z) || 1;
    x /= l; y /= l; z /= l;

    // --- rejections: each a `continue`, in cheapest-first order ---

    // (a) crowding — chord to the nearest ACCEPTED site (prior draws are irrelevant).
    let crowded = false;
    for (const s of sites) {
      const dot = Math.max(-1, Math.min(1, s.dir[0] * x + s.dir[1] * y + s.dir[2] * z));
      if (Math.sqrt(Math.max(0, 2 - 2 * dot)) * R < SITE_MIN_SEPARATION_M) { crowded = true; break; }
    }
    if (crowded) continue;

    // (b) water — underwater or in the surf band.
    if (def.seaRadius !== null && sampler.surfaceRadius(x, y, z) < def.seaRadius + SITE_SEA_MARGIN_M) continue;

    // (c) slope — an aperture needs a pad you can fight on.
    sampler.surfaceNormal(x, y, z, NORMAL, SITE_SLOPE_EPS_M);
    if (NORMAL[0] * x + NORMAL[1] * y + NORMAL[2] * z < SITE_SLOPE_MIN_DOT) continue;

    // (d) town — grown settlement fabric owns its parcels (planetNodes' exact call).
    if (settlementInfluenceAt(sampler, x, y, z, SITE_SETTLEMENT_SLACK_M) !== null) continue;

    sites.push({
      id: cacheSiteId(def.id, i),
      planet: planetIndex,
      dir: [x, y, z],
      tier: siteTierFor(tierRoll),
      theme: siteThemeFor(sampler, x, y, z, themeRoll),
    });
  }
  return sites;
}

/**
 * Resolve one site token against a system's planet roster — the shape
 * `server/src/cache.ts` needs for its `TODO(P6)` `siteExists()` proximity gate and
 * `themeFor()`: parse the token, find the planet whose id it names (identity, never a
 * parallel id resolver — the roster comes from `systemDefFor`), regenerate that planet's
 * catalog and look the site up. Null when the token names no site in this system, which
 * is the "fail loud, mint nothing" answer for an id a client guessed.
 */
export function cacheSiteFor(planets: readonly PlanetDef[], token: unknown): CacheSite | null {
  const parsed = parseSiteId(token);
  if (parsed === null) return null;
  const index = planets.findIndex((p) => p.id === parsed.planetId);
  if (index < 0) return null;
  return cacheSites(planets[index], index).find((s) => s.id === token) ?? null;
}

/** Every cache site in a system, keyed by site token — the P6b lookup shape. */
export function systemCacheSites(planets: readonly PlanetDef[]): Map<string, CacheSite> {
  const map = new Map<string, CacheSite>();
  planets.forEach((p, i) => {
    for (const s of cacheSites(p, i)) map.set(s.id, s);
  });
  return map;
}
